import React from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Flex, 
  Tab, 
  Tabs, 
  TabList, 
  TabPanel, 
  TabPanels,
  Code,
  Divider,
  Button,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import AleaListFilter from '../../components/AleaListFilter/AleaListFilter';
import PageHeader from '../../components/PageHeader/PageHeader';
import { ResidentState } from '../../constants/ResidentState';
import { ResidentFeature } from '../../constants/ResidentFeature';

const AleaListFilterGuide: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Example filter data - same as used in ResidentsPage
  const basicFilters = {
    rstate: [
      {
        label: ResidentState.ENABLED,
        value: ResidentState.ENABLED,
        id: ResidentState.ENABLED,
      },
      {
        label: ResidentState.OUT_OF_CARE,
        value: ResidentState.OUT_OF_CARE,
        id: ResidentState.OUT_OF_CARE,
      },
      {
        label: ResidentState.IN_HOSPITAL,
        value: ResidentState.IN_HOSPITAL,
        id: ResidentState.IN_HOSPITAL,
      },
      {
        label: ResidentState.DISABLED,
        value: ResidentState.DISABLED,
        id: ResidentState.DISABLED,
      },
    ],
    carehome_id: [
      {
        label: 'Care Home 01',
        value: 'Care Home 01',
        id: 'carehome_1',
      },
      {
        label: 'Care Home 02',
        value: 'Care Home 02',
        id: 'carehome_2',
      }
    ],
         station_id: [
       {
         label: 'Living Area 01',
         value: 'Living Area 01',
         id: 'living_area_1',
       },
       {
         label: 'Living Area 02',
         value: 'Living Area 02',
         id: 'living_area_2',
       },
       {
         label: 'Living Area 03',
         value: 'Living Area 03',
         id: 'living_area_3',
       },
       {
         label: 'Living Area 04',
         value: 'Living Area 04',
         id: 'living_area_4',
       },
       {
         label: 'Living Area 05',
         value: 'Living Area 05',
         id: 'living_area_5',
       },
       {
         label: 'Living Area 06',
         value: 'Living Area 06',
         id: 'living_area_6',
       }
     ],
         features: [
       {
         label: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
         value: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
         id: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
       },
       {
         label: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
         value: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
         id: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
       },
       {
         label: ResidentFeature.REPOSITIONING_MANAGEMENT,
         value: ResidentFeature.REPOSITIONING_MANAGEMENT,
         id: ResidentFeature.REPOSITIONING_MANAGEMENT,
       },
       {
         label: ResidentFeature.SECURE_ZONE,
         value: ResidentFeature.SECURE_ZONE,
         id: ResidentFeature.SECURE_ZONE,
       },
       {
         label: ResidentFeature.FALL_MANAGEMENT,
         value: ResidentFeature.FALL_MANAGEMENT,
         id: ResidentFeature.FALL_MANAGEMENT,
       },
     ],
  };



  // Living area with horizontal layout (short names, max 5 items) - Full Basic Filter with horizontal living area
  const horizontalLivingAreaFilters = {
    rstate: [
      {
        label: ResidentState.ENABLED,
        value: ResidentState.ENABLED,
        id: ResidentState.ENABLED,
      },
      {
        label: ResidentState.OUT_OF_CARE,
        value: ResidentState.OUT_OF_CARE,
        id: ResidentState.OUT_OF_CARE,
      },
      {
        label: ResidentState.IN_HOSPITAL,
        value: ResidentState.IN_HOSPITAL,
        id: ResidentState.IN_HOSPITAL,
      },
      {
        label: ResidentState.DISABLED,
        value: ResidentState.DISABLED,
        id: ResidentState.DISABLED,
      },
    ],
    carehome_id: [
      {
        label: 'Care Home 01',
        value: 'Care Home 01',
        id: 'carehome_1',
      },
      {
        label: 'Care Home 02',
        value: 'Care Home 02',
        id: 'carehome_2',
      }
    ],
    station_id: [
      { label: 'W1', value: 'W1', id: 'w1' },
      { label: 'W2', value: 'W2', id: 'w2' },
      { label: 'W3', value: 'W3', id: 'w3' },
      { label: 'E1', value: 'E1', id: 'e1' }
    ],
    features: [
      {
        label: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
        value: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
        id: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
      },
      {
        label: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
        value: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
        id: ResidentFeature.INCONTINENCE_MANAGEMENT_STOOL,
      },
      {
        label: ResidentFeature.REPOSITIONING_MANAGEMENT,
        value: ResidentFeature.REPOSITIONING_MANAGEMENT,
        id: ResidentFeature.REPOSITIONING_MANAGEMENT,
      },
      {
        label: ResidentFeature.SECURE_ZONE,
        value: ResidentFeature.SECURE_ZONE,
        id: ResidentFeature.SECURE_ZONE,
      },
      {
        label: ResidentFeature.FALL_MANAGEMENT,
        value: ResidentFeature.FALL_MANAGEMENT,
        id: ResidentFeature.FALL_MANAGEMENT,
      },
    ],
  };

  // Simple list filters for Hardware page example
  const simpleListFilters = {
    rstate: [
      {
        label: 'Care Home 01',
        value: 'Care Home 01',
        id: 'carehome_1',
      },
      {
        label: 'Care Home 02',
        value: 'Care Home 02',
        id: 'carehome_2',
      },
      {
        label: 'Care Home 03',
        value: 'Care Home 03',
        id: 'carehome_3',
      }
    ]
  };

  return (
    <Flex flexFlow="column">
      <PageHeader title={"List Filter"} />
      <Box p={8} bg="#fff" minH="100vh" borderRadius="20px">
        <Box maxW="1200px" mx="auto" pb="400px">
          <Flex mb={8} align="center">
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={() => navigate('/style-guide')}
              mr={4}
            >
              Back to Style Guide
            </Button>
          </Flex>

            <Text mb={6}>
              AleaListFilter is a versatile filtering component that provides a dropdown menu with multiple filter categories.
              It supports different filter types including status, living area, features, and care home selection.
            </Text>

            <Tabs variant="enclosed" mb={8}>
              <TabList>
                <Tab>Examples</Tab>
                <Tab>Props</Tab>
                <Tab>Usage</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <Box mb={8}>
                    <Heading size="md" mb={4}>Basic Filter</Heading>
                    <Text mb={4}>
                      Complete filter as used in the Residents page with all available filter categories: 
                      status, care home, living area, and features. Click the filter icon to see dropdown options.
                    </Text>
                    <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="flex-end">
                      <AleaListFilter filters={basicFilters} />
                    </Box>
                  </Box>



                  <Divider my={6} />

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Simple List Mode</Heading>
                    <Text mb={4}>
                      Simple list mode without sub-menus, as used in the Hardware page. Perfect for single-level filtering.
                    </Text>
                    <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="flex-end">
                      <AleaListFilter 
                        filters={simpleListFilters} 
                        isSimpleList={true}
                        dropdownWidth="250px" 
                      />
                    </Box>
                  </Box>

                  <Divider my={6} />

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Horizontal Child Layout</Heading>
                    <Text mb={4}>
                      Same as Basic Filter but with horizontal living area layout. When living areas have short names 
                      (less than 4 characters) and there are 5 or fewer items, they display horizontally for better space usage.
                    </Text>
                    <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="flex-end">
                      <AleaListFilter filters={horizontalLivingAreaFilters} />
                    </Box>
                  </Box>




                </TabPanel>

                <TabPanel>
                  <Box mb={8}>
                    <Heading size="md" mb={4}>Props</Heading>
                    <Box overflowX="auto">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Prop</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Default</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>filters</Code></td>
                            <td style={{ padding: '12px' }}><Code>Filters</Code></td>
                            <td style={{ padding: '12px' }}><Code>undefined</Code></td>
                            <td style={{ padding: '12px' }}>Object containing filter options for different categories</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>buttonSize</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}><Code>"50px"</Code></td>
                            <td style={{ padding: '12px' }}>Size of the filter button (width and height)</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>buttonBg</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}><Code>"white"</Code></td>
                            <td style={{ padding: '12px' }}>Background color of the button</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>buttonHoverBg</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}><Code>"#f8f8f8"</Code></td>
                            <td style={{ padding: '12px' }}>Background color of the button on hover</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>dropdownWidth</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}><Code>"186px"</Code></td>
                            <td style={{ padding: '12px' }}>Width of the dropdown menu</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>isSimpleList</Code></td>
                            <td style={{ padding: '12px' }}><Code>boolean</Code></td>
                            <td style={{ padding: '12px' }}><Code>false</Code></td>
                            <td style={{ padding: '12px' }}>Whether to use simple list mode without sub-menus</td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Filters Interface</Heading>
                    <Box overflowX="auto">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Property</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>rstate</Code></td>
                            <td style={{ padding: '12px' }}><Code>FilterOption[]</Code></td>
                            <td style={{ padding: '12px' }}>Status filter options (Active, Inactive, etc.)</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>station_id</Code></td>
                            <td style={{ padding: '12px' }}><Code>FilterOption[]</Code></td>
                            <td style={{ padding: '12px' }}>Living area/station filter options</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>features</Code></td>
                            <td style={{ padding: '12px' }}><Code>FilterOption[]</Code></td>
                            <td style={{ padding: '12px' }}>Feature filter options</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>carehome_id</Code></td>
                            <td style={{ padding: '12px' }}><Code>FilterOption[]</Code></td>
                            <td style={{ padding: '12px' }}>Care home filter options</td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>FilterOption Interface</Heading>
                    <Box overflowX="auto">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Property</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Required</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>label</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}>Yes</td>
                            <td style={{ padding: '12px' }}>Display text for the filter option</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>value</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}>Yes</td>
                            <td style={{ padding: '12px' }}>Value used for filtering logic</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '12px' }}><Code>id</Code></td>
                            <td style={{ padding: '12px' }}><Code>string</Code></td>
                            <td style={{ padding: '12px' }}>Yes</td>
                            <td style={{ padding: '12px' }}>Unique identifier for the option</td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel>
                  <Box mb={8}>
                    <Heading size="md" mb={4}>Basic Usage</Heading>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Step 1: Import the component and constants
import AleaListFilter from '../../components/AleaListFilter/AleaListFilter';
import { ResidentState } from '../../constants/ResidentState';
import { ResidentFeature } from '../../constants/ResidentFeature';

// Step 2: Define complete filter options
const filters = {
  rstate: [
    {
      label: ResidentState.ENABLED,
      value: ResidentState.ENABLED,
      id: ResidentState.ENABLED,
    },
    {
      label: ResidentState.OUT_OF_CARE,
      value: ResidentState.OUT_OF_CARE,
      id: ResidentState.OUT_OF_CARE,
    },
    {
      label: ResidentState.IN_HOSPITAL,
      value: ResidentState.IN_HOSPITAL,
      id: ResidentState.IN_HOSPITAL,
    },
    {
      label: ResidentState.DISABLED,
      value: ResidentState.DISABLED,
      id: ResidentState.DISABLED,
    },
  ],
  carehome_id: [
    {
      label: 'Care Home 01',
      value: 'Care Home 01',
      id: 'carehome_1',
    },
    {
      label: 'Care Home 02',
      value: 'Care Home 02',
      id: 'carehome_2',
    }
  ],
  station_id: [
    {
      label: 'Living Area 01',
      value: 'Living Area 01',
      id: 'living_area_1',
    },
    {
      label: 'Living Area 02',
      value: 'Living Area 02',
      id: 'living_area_2',
    }
  ],
  features: [
    {
      label: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
      value: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
      id: ResidentFeature.INCONTINENCE_MANAGEMENT_URINE,
    },
    {
      label: ResidentFeature.REPOSITIONING_MANAGEMENT,
      value: ResidentFeature.REPOSITIONING_MANAGEMENT,
      id: ResidentFeature.REPOSITIONING_MANAGEMENT,
    }
  ],
};

// Step 3: Use in JSX
<AleaListFilter filters={filters} />`}
                    </Code>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Simple List Mode</Heading>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Simple list without sub-menus - as used in Hardware page
const simpleFilters = {
  rstate: [
    { label: 'Care Home 01', value: 'Care Home 01', id: 'carehome_1' },
    { label: 'Care Home 02', value: 'Care Home 02', id: 'carehome_2' },
    { label: 'Care Home 03', value: 'Care Home 03', id: 'carehome_3' }
  ]
};

<AleaListFilter 
  filters={simpleFilters}
  isSimpleList={true}
  dropdownWidth="250px"
/>`}
                    </Code>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Horizontal Child Layout</Heading>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Full Basic Filter with horizontal living area layout
const horizontalLayoutFilters = {
  // ... same rstate, carehome_id, features as above
  station_id: [
    { label: 'W1', value: 'W1', id: 'w1' },
    { label: 'W2', value: 'W2', id: 'w2' },
    { label: 'W3', value: 'W3', id: 'w3' },
    { label: 'E1', value: 'E1', id: 'e1' }
  ],
};

<AleaListFilter filters={horizontalLayoutFilters} />`}
                    </Code>
                  </Box>



                  <Box mb={8}>
                    <Heading size="md" mb={4}>Living Area Layout Logic</Heading>
                    <Text mb={4}>
                      The component automatically chooses between horizontal and vertical layouts for living areas based on specific criteria.
                    </Text>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Horizontal layout conditions:
// 1. All station names must be less than 4 characters
// 2. Maximum 5 stations
// 3. Only applies to station_id filter

// Horizontal layout example (short names):
const horizontalStations = {
  station_id: [
    { label: 'W1', value: 'W1', id: 'w1' },
    { label: 'W2', value: 'W2', id: 'w2' },
    { label: 'E1', value: 'E1', id: 'e1' }
  ]
};

// Vertical layout example (long names or more than 5 items):
const verticalStations = {
  station_id: [
    { label: 'Living Area 01', value: 'Living Area 01', id: 'living_area_1' },
    { label: 'Living Area 02', value: 'Living Area 02', id: 'living_area_2' },
    { label: 'Living Area 03', value: 'Living Area 03', id: 'living_area_3' }
  ]
};`}
                    </Code>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>Custom Styling</Heading>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// Custom styled filter with different button size and colors
<AleaListFilter 
  filters={filters}
  buttonSize="60px"
  buttonBg="#f5f5f5"
  buttonHoverBg="#e5e5e5"
  dropdownWidth="220px"
/>`}
                    </Code>
                  </Box>

                  <Box mb={8}>
                    <Heading size="md" mb={4}>URL Integration</Heading>
                    <Text mb={4}>
                      The component automatically handles URL query parameters for filter state management.
                      Selected filters are reflected in the URL and persist across page reloads.
                    </Text>
                    <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
{`// URL will be updated automatically when filters are selected
// Example: /residents?rstate=active,pending&station_id=w1,w2

// The component reads from location.search on mount
// and updates URL when filters change`}
                    </Code>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>            
      </Flex>
  );
};

export default AleaListFilterGuide; 